import { useCallback, useMemo,useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { notification } from 'antd';

import ExceptionService from '@src/services/ExceptionService';

const useAbsences = () => {
  const intl = useIntl();
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get organization and user from Redux
  const organizationId = useSelector(({ organizations }) => organizations.organization.id);

  // Memoize service instance to prevent recreation on every render
  const exceptionService = useMemo(() => new ExceptionService(), []);

  const createAbsence = useCallback(
    async absenceData => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        const dataToCreate = {
          ...absenceData,
        };

        const result = await exceptionService.createException(dataToCreate, organizationId);

        // Add to local state for immediate UI update
        if (result && result.id) {
          const newAbsence = {
            ...dataToCreate,
            id: result.id,
            startDate: absenceData.startDate,
            endDate: absenceData.endDate,
          };
          setAbsences(prev => [newAbsence, ...prev]);
        }

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        return { success: true, data: result };
      } catch (error) {
        console.error('Error creating absence:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  const updateAbsence = useCallback(
    async (absenceId, absenceData) => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        const result = await exceptionService.updateException(
          absenceId,
          absenceData,
          organizationId,
        );

        // Update local state for immediate UI update
        setAbsences(prev =>
          prev.map(absence =>
            absence.id === absenceId
              ? {
                  ...absence,
                  ...absenceData,
                  startDate: absenceData.startDate,
                  endDate: absenceData.endDate,
                }
              : absence,
          ),
        );

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        return { success: true, data: result };
      } catch (error) {
        console.error('Error updating absence:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  const deleteAbsence = useCallback(
    async absenceId => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        await exceptionService.deleteException(absenceId, organizationId);

        // Remove from local state for immediate UI update
        setAbsences(prev => prev.filter(absence => absence.id !== absenceId));

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        return { success: true };
      } catch (error) {
        console.error('Error deleting absence:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  const fetchAbsences = useCallback(
    async (filters = {}) => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        const result = await exceptionService.getAllExceptions(organizationId, filters);
        // Convert Absence model instances to plain objects for state
        const plainAbsences = result.map(absence => (absence.toJson ? absence.toJson() : absence));
        setAbsences(plainAbsences);

        return { success: true, data: plainAbsences };
      } catch (error) {
        console.error('Error fetching absences:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  const fetchAbsencesByUser = useCallback(
    async userId => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        const result = await exceptionService.getExceptionsByUserId(userId, organizationId);

        // Convert Absence model instances to plain objects
        const plainAbsences = result.map(absence => (absence.toJson ? absence.toJson() : absence));

        return { success: true, data: plainAbsences };
      } catch (error) {
        console.error('Error fetching user absences:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  const getAbsenceStats = useCallback(
    async (dateRange = null) => {
      setLoading(true);
      try {
        if (!organizationId) {
          throw new Error('Organization not found');
        }

        const stats = await exceptionService.getExceptionStats(organizationId, dateRange);

        return { success: true, data: stats };
      } catch (error) {
        console.error('Error fetching absence stats:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return { success: false, error: error.message };
      } finally {
        setLoading(false);
      }
    },
    [intl, organizationId, exceptionService],
  );

  return {
    absences,
    loading,
    createAbsence,
    updateAbsence,
    deleteAbsence,
    fetchAbsences,
    fetchAbsencesByUser,
    getAbsenceStats,
  };
};

export default useAbsences;
